import { BriefingItemDto, ValueImpact } from '../../types/opsBriefing';
import { computeMemberValue } from './memberValue';
import { resolvePassOrder } from './passOrder';
import { itemPassOrder } from './adapters';
import { isSessionless } from './sessionless';

/**
 * 브리핑 항목의 금액을 붙인다.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 이 파일이 필요한가 — 서버 작업이 아니었다
 * ─────────────────────────────────────────────────────────────
 * 「이번 달 브리핑이 지킨 매출」은 이 기능의 수익화 근거인데, 오늘의 브리핑에서
 * 금액이 늘 비어 있었습니다. 원인을 서버로 짚어 두고 있었는데 **틀렸습니다.**
 *
 * 계약(`types/opsBriefing.ts`)이 이미 이렇게 정해 뒀습니다:
 *
 *   value?: ValueImpact | null;
 *   ⚠️ 서버는 계산하지 마세요. null 로 주시면 됩니다.
 *      금액 계산은 프론트 memberValue.ts 한 곳에만 둡니다 —
 *      서버와 프론트 양쪽에서 계산하면 두 화면의 숫자가 갈립니다.
 *
 * 그리고 서버는 **단가 원본을 이미 보내고 있습니다** (`item.membership`).
 * 계산기도 이미 있습니다 (`memberValue.ts`) — 회원 현황 탭은 `adapters.ts` 를
 * 거쳐 이미 쓰고 있습니다. **오늘의 브리핑만 그 경로를 안 탔습니다.**
 * `OpsBriefingTodayPage` 는 `item.value` 를 그대로 읽었고, 서버가 계약대로
 * 그 자리를 null 로 두니 헤더 합계도 카드 금액 태그도 늘 비어 있었습니다.
 *
 * 그래서 서버 작업이 아니라 여기 한 곳을 잇는 일입니다.
 * 회원 현황과 **같은 계산기**를 쓰므로 두 화면의 금액이 갈릴 수 없습니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 이 파일이 지켜야 하는 것
 * ─────────────────────────────────────────────────────────────
 * ① **금액을 만들어내지 않는다.** 단가가 없으면 `null` 입니다.
 *    0원으로 적으면 「가치 없는 회원」이 되고, 임의값을 넣으면 거짓말이 됩니다.
 *    0원 단가(라포 도입 전 이관 건)도 `null` 과 같게 봅니다 — 계산기의
 *    `priced()` 규칙 그대로이고, 회원 현황의 「+N명 산정 불가」와 같은 규칙입니다.
 * ② **회차 개념이 없는 수강권(무제한·대관)은 금액을 세지 않는다.**
 *    판정은 `sessionless.isSessionless` 한 곳에서만 합니다 (아래 주석).
 * ③ **순번은 프론트가 판정한다.** 서버가 준 순번 **판정**을 믿지 않습니다 —
 *    계산이 두 곳에 있으면 회원 현황의 뱃지와 여기 금액 근거가 갈립니다.
 *    다만 **원본 기록**(`membership.memberships[]`)은 서버가 주는 것을 씁니다.
 *    회원 현황과 같은 함수에 태워야 두 화면의 요율이 같아집니다.
 */

/**
 * 이 등급에서 재등록을 기대해도 되는가 — 정책 M-D2.
 *
 * `adapters.expectsReRegistration` 과 **같은 판정**입니다.
 * 그쪽은 회원 단계(STAGE)로, 여기는 브리핑 등급(GRADE)으로 봅니다 —
 * 브리핑 항목에는 단계를 계산할 원본(pace·공백)이 실려 있지 않기 때문입니다.
 * 대응은 이렇습니다: `DANGER ↔ 이탈 위험` · `WARNING ↔ 주의·휴면`.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 휴면(`WARNING` · `dormant-` 항목)의 잔여 선수금은 **그대로 셉니다**
 * ─────────────────────────────────────────────────────────────
 * 2026-08-27 백엔드 2차부터 공백 45일 초과 회원이 `WARNING` 항목으로 올라옵니다.
 * 그 회원의 미사용 선수금은 「지켜야 할 매출」에 그대로 들어갑니다.
 *
 *   · 그 돈은 **이미 받은 돈**입니다. 회원이 잔여 회차를 쥔 채 안 나오는 것이고,
 *     환불·소멸로 나가면 센터가 실제로 잃습니다.
 *   · 합계에서 빼면 틀립니다. 잔여가 **실재**하므로 0원도 아니고 「모름」도 아닙니다.
 *
 * 달라지는 것은 **재등록 기대**입니다. 두 달 넘게 안 나온 회원에게
 * 「재등록 63.5% 기대」를 곱할 근거가 없으므로 0 으로 둡니다.
 */
export const expectsReRegistrationByGrade = (grade: BriefingItemDto['grade']): boolean =>
    grade === 'CHANCE' || grade === 'PRAISE';

/**
 * 항목 하나의 금액. 못 내면 `null`.
 *
 * `null` 이 되는 경우 — 전부 「모른다」이지 「0원」이 아닙니다:
 *   · 회원 항목이 아니다 (강사·센터)
 *   · 서버가 수강권 원본을 안 보냈다
 *   · 단가가 없다 (무료권·서비스 세션·이관 건 · 0원 포함)
 *   · 회차 개념이 없는 수강권이다 (무제한·대관)
 */
export const valueOfItem = (item: BriefingItemDto): ValueImpact | null => {
    /*
     * 서버가 이미 계산해 보냈으면 그대로 씁니다.
     * 계약상 null 이 정상이지만, 나중에 서버가 채우기로 바뀌어도
     * 이 화면이 두 번 계산하지 않게 합니다.
     *
     * 🔴 **새 계약(`prepaid`/`expected`)을 갖춘 값만 믿습니다** (교차검증 지적).
     *    옛 계약 `{kind, amount, label}` 이 실린 문서(캐시된 응답·구버전 서버)를
     *    그대로 받으면 두 키가 `undefined` 라 합계에 **0 으로 들어갑니다** —
     *    금액이 있는데도 조용히 사라지고, 「금액 미산정」에도 안 잡힙니다.
     *    모양이 안 맞으면 무시하고 원본으로 다시 계산합니다.
     */
    if (item.value && ('prepaid' in item.value || 'expected' in item.value)) return item.value;

    if (item.subjectType !== 'MEMBER') return null;

    const m = item.membership;
    if (!m) return null;

    /*
     * 🔴 회차 개념이 없는 수강권(무제한·대관)은 금액을 세지 않습니다.
     *
     * ⚠️ `totalSession >= 999` 를 여기 적지 않습니다. 판정은 `sessionless.ts`
     *    한 곳에서만 합니다 — 화면마다 적으면 반드시 갈리고, 그러면 같은 회원이
     *    목록에서는 「무제한」인데 합계에는 3천만 원으로 들어갑니다.
     *    (이식 원본은 `memberRollup.UNLIMITED_SESSION_THRESHOLD` 라는 별도 상수를
     *     썼는데, 그건 서버가 명시 플래그를 보내기 시작해도 못 알아듣습니다.)
     *
     * `computeMemberValue` 도 안에서 같은 판정을 하지만 여기서 한 번 더 거릅니다.
     * 아래 `sessionless` 를 계산기에 넘기는 것이 본질이고, 이 줄은 의도를 드러내는
     * 자리입니다 — 둘 중 하나가 빠져도 999 × 단가가 새어 나가지 않게 합니다.
     */
    if (isSessionless(m)) return null;

    /*
     * 🔴 **순번 원본으로 재등록률을 정합니다** (2026-08-31 감사 발견 A 수정).
     *
     * ─────────────────────────────────────────────────────────────
     * 예전에는 `resolvePassOrder([])` — 빈 배열이었습니다
     * ─────────────────────────────────────────────────────────────
     * 서버가 순번 원본을 안 보내던 시절의 코드가 그대로 남아 있었습니다.
     * 빈 배열은 「모른다」(basis: 'NONE', order 0)라서 계산기가 **기본값
     * 63.5%** 를 썼고, 그게 **전원에게** 적용됐습니다.
     *
     * ⚠️ 그 기본값은 **가장 높은 재등록률**입니다(1번째 52.9 · 2번째 53.4 ·
     *    3번째 63.5). 옛 주석은 이걸 「보수적」이라고 적었지만 **사실과 반대**로,
     *    1·2번째 회원의 금액을 부풀리고 있었습니다.
     *    8/28 실데이터 검산에서 헤더 합계가 회원 현황보다 **1,121,650원(8.2%)**
     *    높게 나왔고, 그게 이 한 줄 때문이었습니다.
     *
     * 서버는 백엔드 1차부터 `membership.memberships[]` 를 싣고 있습니다.
     * 회원 현황과 **같은 함수**(`adapters.itemPassOrder` → `resolvePassOrder`)를
     * 태워서 두 화면이 같은 순번·같은 요율을 보게 합니다.
     *
     * ⚠️ 원본이 없는 항목(옛 문서·이관 결손)은 예전처럼 기본값으로 떨어집니다.
     *    그때의 63.5% 는 「보수적」이 아니라 **셋 중 가장 높은 값을 쓰는 것**이니,
     *    그 항목의 금액은 위쪽으로 치우쳐 있다고 읽어야 합니다.
     *    (검증 구간 밖인 4번째 이상에서는 63.5% 가 진짜 하한선이라 사정이 다릅니다
     *     — `memberValue.FALLBACK_REREGISTRATION_RATE` 주석 참고)
     */
    const passOrder = itemPassOrder(item) ?? resolvePassOrder([]);

    const v = computeMemberValue(
        {
            remain: m.remain,
            pricePerSchedule: m.pricePerSchedule,
            totalPrice: m.totalPrice,
            totalSession: m.totalSession ?? 0,
            /*
             * 서버가 명시한 무제한 여부. 아직 안 보내지만(키 없음 = 모름),
             * 보내기 시작하면 **회차 추측보다 이 값이 이깁니다.**
             * 빠뜨리면 `false` 로 명시한 진짜 999회 수강권의 금액이 통째로 사라집니다.
             */
            sessionless: m.sessionless,
        },
        passOrder,
        { expectsReRegistration: expectsReRegistrationByGrade(item.grade) }
    );
    if (!v) return null;

    /*
     * 🔴 계약이 요구하는 형태로 맞춥니다.
     *    `MemberValue`(계산기) 와 `ValueImpact`(화면 계약) 는 다른 타입입니다 —
     *    전자에만 `rateVerified` 가 있습니다. 그대로 넘기면 컴파일이 깨집니다.
     */
    return {
        prepaid: v.prepaid,
        expected: v.expected,
        breakdown: v.breakdown,
        /* 「왜 이 금액인가」 팝업이 한 줄씩 늘어놓는 근거 — 계산기가 만든 것을 그대로 나른다 */
        prepaidBasis: v.prepaidBasis,
        expectedBasis: v.expectedBasis,
    };
};

/**
 * 회원마다 항목 하나만 남긴다 — **재등록 기대를 한 번만 세기 위해** (정책 M7).
 *
 * 브리핑 항목에는 `memberRollup` 이 없어 대표 수강권을 물어볼 곳이 없습니다.
 * 그래서 회원 기준으로 **기대가 가장 큰 항목**만 남깁니다. 배열 순서를 보지
 * 않으므로 다시 받아와도 합계가 흔들리지 않습니다
 * (`adapters.pickPrimaryCards` 와 같은 규칙 — 폴백 갈래가 여기서는 본 갈래입니다).
 *
 * 같은 회원이 하루에 두 번 올라오는 일은 편성에서 거의 없지만, 「거의」로 두면
 * 그날 헤더의 「확보 가능한 매출」만 조용히 두 배가 됩니다.
 */
const dedupeBySubject = <T extends BriefingItemDto>(items: T[]): T[] => {
    const best = new Map<string, T>();
    items.forEach((item) => {
        const key = item.subjectId || item.itemId;
        const cur = best.get(key);
        if (!cur) {
            best.set(key, item);
            return;
        }
        const a = cur.value?.expected ?? 0;
        const b = item.value?.expected ?? 0;
        if (b > a || (b === a && item.itemId < cur.itemId)) best.set(key, item);
    });
    return Array.from(best.values());
};

/**
 * 「지켜야 할 매출」에 **실제로 더해진 항목** — 합계를 만든 바로 그 목록.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 목록을 내보내나 (2026-09-01 QA31)
 * ─────────────────────────────────────────────────────────────
 * 금액을 누르면 「누가 얼마씩 들어갔는지」를 펴는 팝업이 생겼습니다. 그 목록을
 * 화면이 따로 만들면(`items.filter(...)`) 합계를 만든 목록과 갈릴 수 있고,
 * 그러면 내역을 더한 값이 헤더와 안 맞습니다 — 근거를 보여주려고 연 팝업이
 * 오히려 숫자를 못 믿게 만듭니다. 그래서 **합계와 같은 함수가 목록도** 냅니다.
 *
 * `withValues` 를 이미 지난 목록이 들어와도 같은 답입니다 — `valueOfItem` 은
 * 이미 채워진 값을 그대로 씁니다(재계산 아님).
 */
export const prepaidContributors = <T extends BriefingItemDto>(items: T[]): T[] =>
    items.filter((i) => i.subjectType === 'MEMBER').map((i) => ({ ...i, value: valueOfItem(i) }));

/**
 * 「확보 가능한 매출」에 실제로 더해진 항목 — **회원당 한 건**입니다 (정책 M7).
 *
 * ⚠️ `prepaidContributors` 와 **분모가 다릅니다.** 이쪽만 회원 중복을 걷어냅니다.
 */
export const expectedContributors = <T extends BriefingItemDto>(items: T[]): T[] =>
    dedupeBySubject(prepaidContributors(items));

/**
 * 헤더의 두 숫자 — 정책 M2 · M4.
 *
 * 🔴 **분모가 다릅니다.** 선수금은 항목마다 다른 수강권이라 전부 더하고,
 *    재등록 기대는 회원당 한 번만 셉니다. 한 함수 안에서 갈라 두는 것은
 *    부르는 쪽마다 한쪽을 잊지 않게 하기 위해서입니다.
 */
export const briefingTotals = (items: BriefingItemDto[]): { prepaid: number; expected: number } => ({
    prepaid: prepaidContributors(items).reduce((s, i) => s + (i.value?.prepaid ?? 0), 0),
    expected: expectedContributors(items).reduce((s, i) => s + (i.value?.expected ?? 0), 0),
});

/**
 * 항목 목록에 금액을 붙인다. **원본은 건드리지 않는다.**
 *
 * 오늘의 브리핑은 이 함수 하나만 부르면 됩니다 — 헤더 합계(`sumValueByKind`),
 * 카드 금액 태그(`BriefingCard.ValueTag`), 지시에 실리는 금액(`orderValueOf`)이
 * 전부 `item.value` 를 읽으므로 입구에서 한 번 채우면 전부 이어집니다.
 */
export const withValues = <T extends BriefingItemDto>(items: T[]): T[] =>
    items.map((item) => ({ ...item, value: valueOfItem(item) }));

export type ValueSummary = {
    /** 💰 지켜야 할 매출 — Σ 미사용 선수금 (정책 M2) */
    prepaid: number;
    /** 📈 확보 가능한 매출 — Σ 재등록 기대, **회원당 1회** (정책 M4·M7) */
    expected: number;
    /**
     * 🔴 금액을 못 낸 **회원 항목** 수.
     *
     * 화면이 이걸 반드시 밝혀야 합니다. 안 밝히면 원장님은 합계를
     * 「센터 전체」로 읽는데 사실은 일부만 센 것입니다.
     */
    unpriced: number;
    /** 금액을 낸 회원 항목 수 */
    priced: number;
};

/**
 * 합계.
 *
 * 🔴 못 낸 건을 0원으로 더하지 않습니다. 합계는 같아 보이지만
 * 「챙길 게 없는 건」으로 묻힙니다 — `adapters.ts` 가 같은 이유로
 * `countUnpriced` 를 따로 두고 있습니다.
 *
 * 강사·센터 항목은 분모에서 아예 뺍니다. 원래 금액이 없는 항목이라
 * 「못 낸 건」으로 세면 커버리지가 왜곡됩니다.
 */
export const summarizeValue = (items: BriefingItemDto[]): ValueSummary => {
    /*
     * `withValues` 를 이미 지난 목록이 들어올 수도, 원본이 들어올 수도 있습니다.
     * 여기서 한 번 더 채워 두면 두 경우가 같은 답을 냅니다 — `valueOfItem` 은
     * `item.value` 가 이미 있으면 그대로 씁니다(재계산 아님).
     */
    const priced = prepaidContributors(items);

    const totals = briefingTotals(priced);
    return {
        prepaid: totals.prepaid,
        expected: totals.expected,
        unpriced: priced.filter((i) => i.value == null).length,
        priced: priced.filter((i) => i.value != null).length,
    };
};

/**
 * 지시로 실제 지킨 금액 — 원장이 처리한 건만 셉니다.
 *
 * 🔴 「지켜낸 매출 0원」과 「아직 집계 전」은 다릅니다.
 * 처리한 건이 하나도 없으면 0원이 맞지만, 금액을 못 낸 건만 있으면
 * 0원이 아니라 **모르는 것**입니다. 화면이 구분할 수 있게 둘 다 돌려줍니다.
 */
export const actedValue = (items: BriefingItemDto[]): ValueSummary =>
    summarizeValue(items.filter((i) => i.status === 'ACTED'));
